import React from "react";
import { observer } from "mobx-react";
import { Alert, Button, Input, InputNumber, Select, DatePicker } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import moment from "moment";

import alertIcon from "./alert-icon";
import {
    commaListText,
    fieldBlocks,
    isRequired,
    isVisible,
    itemEntries,
    itemSource,
} from "./fields";

import "./style.scoped.scss";

const YES_NO_OPTIONS = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
];

const EMPTY_ROW = "";
const RANGE_START = 0;
const RANGE_END = 1;
const FIRST_ITEM = 0;

const toOptions = (options = []) =>
    options.map((o) => (typeof o === "object" ? o : { value: o, label: o }));

const popupInParent = (trigger) => trigger.parentElement;

const FieldControl = observer(({ field, values, setValue, onRemoveRow }) => {
    const value = values[field.name];

    const setMoment = (m) => {
        setValue(field.name, m ? m.toISOString() : null);
    };
    const setRangeEnd = (position, text) => {
        const range = [value?.[RANGE_START] || "", value?.[RANGE_END] || ""];
        range[position] = text;
        setValue(field.name, range);
    };

    switch (field.type) {
        case "text":
            return (
                <Input
                    value={value || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "textarea":
            return (
                <Input.TextArea
                    rows={2}
                    value={value || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "long_text":
            return (
                <Input.TextArea
                    rows={4}
                    value={value || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "number":
            return (
                <InputNumber
                    value={value ?? undefined}
                    placeholder={field.placeholder}
                    precision={field.precision}
                    min={field.min}
                    onChange={(v) => setValue(field.name, v)}
                />
            );
        case "boolean":
            return (
                <div className="cer-yes-no">
                    {YES_NO_OPTIONS.map((option) => (
                        <Button
                            key={option.label}
                            type={value === option.value ? "primary" : "default"}
                            onClick={() =>
                                setValue(field.name, value === option.value ? null : option.value)
                            }
                        >
                            {option.label}
                        </Button>
                    ))}
                </div>
            );
        case "select":
            return (
                <Select
                    value={value || undefined}
                    placeholder={field.placeholder || "Select"}
                    allowClear
                    getPopupContainer={popupInParent}
                    onChange={(v) => setValue(field.name, v || null)}
                    options={toOptions(field.options)}
                />
            );
        case "date":
            return (
                <DatePicker
                    value={value ? moment(value) : null}
                    getPopupContainer={popupInParent}
                    onChange={setMoment}
                />
            );
        case "text_range":
            return (
                <div className="cer-text-range">
                    <Input
                        value={value?.[RANGE_START] || ""}
                        placeholder={field.placeholders?.[RANGE_START]}
                        onChange={(e) => setRangeEnd(RANGE_START, e.target.value)}
                    />
                    <Input
                        value={value?.[RANGE_END] || ""}
                        placeholder={field.placeholders?.[RANGE_END]}
                        onChange={(e) => setRangeEnd(RANGE_END, e.target.value)}
                    />
                </div>
            );
        case "string_list":
            return (
                <StringList
                    value={value}
                    placeholder={field.placeholder}
                    onChange={(v) => setValue(field.name, v)}
                    onRemoveRow={onRemoveRow}
                />
            );
        case "comma_list":
            return (
                <Input
                    value={commaListText(value)}
                    placeholder={field.placeholder}
                    onChange={(e) => setValue(field.name, e.target.value)}
                />
            );
        case "clinical_entry_list":
            return <ClinicalEntryList value={value} onChange={(v) => setValue(field.name, v)} />;
        default:
            return null;
    }
});

const StringList = ({ value, placeholder, onChange, onRemoveRow }) => {
    const stored = Array.isArray(value) ? value : [];
    const list = stored.length ? stored : [EMPTY_ROW];

    const update = (i, v) => {
        const next = [...list];
        next[i] = v;
        onChange(next);
    };
    const remove = (i) => {
        const next = [...list];
        next.splice(i, 1);
        onChange(next);
        if (onRemoveRow) {
            onRemoveRow(i);
        }
    };
    const add = () => {
        onChange([...list, EMPTY_ROW]);
    };

    return (
        <div className="cer-string-list">
            {list.map((v, i) => (
                <div className="string-row" key={i}>
                    <Input
                        value={v}
                        placeholder={placeholder}
                        onChange={(e) => update(i, e.target.value)}
                    />
                    <Button
                        className="remove-btn"
                        icon={<CloseOutlined />}
                        onClick={() => remove(i)}
                    />
                </div>
            ))}
            <Button className="add-btn" icon={<PlusOutlined />} onClick={add}>
                Add
            </Button>
        </div>
    );
};

const ClinicalEntryList = ({ value, onChange }) => {
    const list = Array.isArray(value) ? value : [];

    const update = (i, key, v) => {
        const next = [...list];
        next[i] = { ...next[i], [key]: v };
        onChange(next);
    };
    const remove = (i) => {
        const next = [...list];
        next.splice(i, 1);
        onChange(next);
    };
    const add = () => {
        onChange([...list, { name: "" }]);
    };

    return (
        <div className="cer-entry-list">
            {list.map((entry, i) => (
                <div className="entry" key={i}>
                    <Input
                        placeholder="Name (required)"
                        value={entry?.name || ""}
                        onChange={(e) => update(i, "name", e.target.value)}
                    />
                    <Input
                        placeholder="Device (optional)"
                        value={entry?.device || ""}
                        onChange={(e) => update(i, "device", e.target.value)}
                    />
                    <Input
                        placeholder="Source document (optional)"
                        value={entry?.source_document || ""}
                        onChange={(e) => update(i, "source_document", e.target.value)}
                    />
                    <Button
                        className="remove-btn"
                        icon={<CloseOutlined />}
                        onClick={() => remove(i)}
                    />
                </div>
            ))}
            <Button className="add-btn" icon={<PlusOutlined />} onClick={add}>
                Add
            </Button>
        </div>
    );
};

const PerItemControl = observer(({ field, values, setValue, required }) => {
    const source = itemSource(field, values);
    const entries = itemEntries(values[field.name]);

    const setEntry = (index, entry) => {
        const next = [...entries];
        while (next.length <= index) {
            next.push("");
        }
        next[index] = entry || "";
        setValue(field.name, next);
    };

    const control = (index) => (
        <FieldControl
            field={{ ...field, name: index, perItemOf: null }}
            values={entries}
            setValue={setEntry}
        />
    );

    if (source.length <= 1) {
        return control(FIRST_ITEM);
    }

    return (
        <div className="cer-per-item">
            {source.map((name, index) => (
                <div className="item" key={index}>
                    <div className="item-name">
                        {(name || "").trim() || `Device ${index + 1}`}
                        {required && !field.everyItem && index > FIRST_ITEM && (
                            <span className="optional"> (optional)</span>
                        )}
                    </div>
                    {control(index)}
                </div>
            ))}
        </div>
    );
});

const Field = observer(({ field, values, setValue, invalid, dependents, advisories }) => {
    const required = isRequired(field, values);

    const removeAlignedEntries = (index) => {
        dependents.forEach((dependent) => {
            const entries = itemEntries(values[dependent.name]);
            if (index < entries.length) {
                setValue(
                    dependent.name,
                    entries.filter((_, i) => i !== index),
                );
            }
        });
    };

    return (
        <div className={`cer-field${invalid ? " invalid" : ""}`}>
            {!field.hideLabel && (
                <label>
                    {field.label}
                    {required ? <span className="required-marker"> *</span> : null}
                </label>
            )}
            {field.description && <div className="field-description">{field.description}</div>}
            {field.perItemOf ? (
                <PerItemControl
                    field={field}
                    values={values}
                    setValue={setValue}
                    required={required}
                />
            ) : (
                <FieldControl
                    field={field}
                    values={values}
                    setValue={setValue}
                    onRemoveRow={removeAlignedEntries}
                />
            )}
            {invalid && <div className="field-error">{field.label} is required.</div>}
            {advisories.map(({ field: _field, ...a }, i) => (
                <Alert
                    key={i}
                    className="field-advisory"
                    showIcon
                    icon={alertIcon(a.type)}
                    {...a}
                />
            ))}
        </div>
    );
});

const groupByRow = (fields) => {
    const groups = [];
    for (const field of fields) {
        const last = groups[groups.length - 1];
        if (field.row && last && last.row === field.row) {
            last.fields.push(field);
        } else {
            groups.push({ row: field.row || null, fields: [field] });
        }
    }
    return groups;
};

const FieldList = observer(
    ({
        template,
        section,
        values,
        setValue,
        selectedSections = [],
        block = null,
        hideBlockTitles = false,
        invalid = [],
        advisories = [],
    }) => {
        const selected = new Set(selectedSections);
        const invalidNames = new Set(invalid);

        const blocks = fieldBlocks(template, section).filter(
            (b) => block === null || b.id === block,
        );

        return (
            <>
                {blocks.map((fieldBlock) => {
                    const { id } = fieldBlock;
                    const limitedTo = template.blockSections[id];
                    if (limitedTo && !limitedTo.some((key) => selected.has(key))) {
                        return null;
                    }
                    return (
                        <div className="cer-field-block" key={id}>
                            {!hideBlockTitles && template.blockTitles[id] && (
                                <h4>{template.blockTitles[id]}</h4>
                            )}
                            {template.blockDescriptions[id] && (
                                <div className="block-description">
                                    {template.blockDescriptions[id]}
                                </div>
                            )}
                            {groupByRow(
                                fieldBlock.fields.filter((field) => isVisible(field, values)),
                            ).map((group) => {
                                const fields = group.fields.map((field) => (
                                    <Field
                                        key={field.name}
                                        field={field}
                                        values={values}
                                        setValue={setValue}
                                        invalid={invalidNames.has(field.name)}
                                        dependents={template.fields.filter(
                                            (f) => f.perItemOf === field.name,
                                        )}
                                        advisories={advisories.filter(
                                            (a) => a.field === field.name,
                                        )}
                                    />
                                ));
                                return group.row ? (
                                    <div className="cer-field-row" key={group.row}>
                                        {fields}
                                    </div>
                                ) : (
                                    fields
                                );
                            })}
                        </div>
                    );
                })}
            </>
        );
    },
);

export default FieldList;
